import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import { AnimatedHighcharts } from './AnimatedHighcharts';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './DesignSystem.css';

/* ================================================================
   Begin - Design System
   Accesible solo por URL directa: /DS
   ================================================================ */

// --------------- DATA ---------------

const SECTIONS = {
  foundations: [
    { id: 'colors', label: 'Colores' },
    { id: 'typography', label: 'Tipografia' },
    { id: 'tokens', label: 'Tokens CSS' },
    { id: 'spacing', label: 'Espaciado' },
    { id: 'grid', label: 'Grid 12x12' },
  ],
  components: [
    { id: 'cards', label: 'Cards' },
    { id: 'buttons', label: 'Botones' },
    { id: 'dropdowns', label: 'Dropdowns' },
    { id: 'alerts', label: 'Alertas & Status' },
    { id: 'charts', label: 'Charts (Highcharts)' },
    { id: 'maps', label: 'Maps (Mapbox)' },
    { id: 'navigation', label: 'Navegacion' },
    { id: 'scrollbar', label: 'Scrollbar' },
    { id: 'loading', label: 'Estados de carga' },
    { id: 'icons', label: 'Iconos' },
  ],
  guidelines: [
    { id: 'naming', label: 'Convenciones CSS' },
  ],
};

const MAIN_COLORS = [
  { name: 'Brand 900', value: '#0E1323', token: '--color-brand-900', usage: 'Fondo principal' },
  { name: 'Accent 500', value: '#F18F05', token: '--color-accent-500', usage: 'Acento de hover/cta' },
  { name: 'Accent 700', value: '#CC6315', token: '--color-accent-700', usage: 'Primary default' },
  { name: 'Neutral 800', value: '#3E3A39', token: '--color-neutral-800', usage: 'Superficies oscuras' },
  { name: 'Neutral 600', value: '#716F6F', token: '--color-neutral-600', usage: 'Bordes y apoyo' },
  { name: 'Neutral 200', value: '#D8D4D2', token: '--color-neutral-200', usage: 'Texto principal' },
];

const STATUS_COLORS = [
  { name: 'Success', value: '#4CAF50', token: '--color-status-success', usage: 'KPIs positivos' },
  { name: 'Warning', value: '#F59E0B', token: '--color-status-warning', usage: 'Alertas amarillas' },
  { name: 'Warning Alto', value: '#F97316', token: '--color-status-warning-high', usage: 'Alertas naranjas' },
  { name: 'Error', value: '#EF4444', token: '--color-status-error', usage: 'Alertas rojas' },
  { name: 'Error Alt', value: '#F44336', token: '--color-status-error-alt', usage: 'Barras en alerta' },
];

const BG_COLORS = [
  { name: 'Body BG', value: '#0E1323', token: '--color-bg-body', usage: 'Fondo base de la app' },
  { name: 'Card BG', value: '#3E3A3966', token: '--color-bg-card', usage: 'Superficie de cards' },
  { name: 'Overlay Light', value: '#716F6F33', token: '--color-bg-overlay-light', usage: 'Hover e inputs' },
  { name: 'Overlay Active', value: '#716F6F55', token: '--color-bg-overlay-active', usage: 'Focus y activo' },
  { name: 'Sidebar', value: '#0E1323EB', token: '--color-bg-sidebar', usage: 'TopBar y SideNav' },
  { name: 'Input Dark', value: '#3E3A39', token: '--color-bg-input-dark', usage: 'Fondos de selects' },
];

const TEXT_COLORS = [
  { name: 'Primary', value: '#D8D4D2', token: '--color-text-primary', usage: 'Texto principal' },
  { name: 'Secondary', value: '#D8D4D2E6', token: '--color-text-secondary', usage: 'Texto secundario' },
  { name: 'Tertiary', value: '#D8D4D2B3', token: '--color-text-tertiary', usage: 'Texto de apoyo' },
  { name: 'Muted', value: '#D8D4D280', token: '--color-text-muted', usage: 'Meta e informacion suave' },
  { name: 'Disabled', value: '#D8D4D259', token: '--color-text-disabled', usage: 'Estado deshabilitado' },
];

const GLOBAL_TOKENS = [
  { token: '--color-brand-900', value: '#0E1323', desc: 'Brand base oscuro' },
  { token: '--color-accent-500', value: '#F18F05', desc: 'Acento principal claro' },
  { token: '--color-accent-700', value: '#CC6315', desc: 'Acento principal intenso' },
  { token: '--color-neutral-800', value: '#3E3A39', desc: 'Neutral oscuro' },
  { token: '--color-neutral-600', value: '#716F6F', desc: 'Neutral medio' },
  { token: '--color-neutral-200', value: '#D8D4D2', desc: 'Neutral claro' },
  { token: '--color-status-success', value: '#4CAF50', desc: 'Estado positivo' },
  { token: '--color-status-warning', value: '#F59E0B', desc: 'Estado warning' },
  { token: '--color-status-warning-high', value: '#F97316', desc: 'Warning alto' },
  { token: '--color-status-error', value: '#EF4444', desc: 'Estado error' },
  { token: '--color-status-error-alt', value: '#F44336', desc: 'Error alternativo' },
  { token: '--status-success-deg', value: 'linear-gradient(to left, #4CAF50, transparent)', desc: 'Degradado estado verde' },
  { token: '--status-warning-deg', value: 'linear-gradient(to left, #F97316, transparent)', desc: 'Degradado estado naranja' },
  { token: '--status-error-deg', value: 'linear-gradient(to left, #EF4444, transparent)', desc: 'Degradado estado rojo' },
  { token: '--color-bg-body', value: '#0E1323', desc: 'Fondo principal app' },
  { token: '--color-bg-card', value: '#3E3A3966', desc: 'Fondo de card' },
  { token: '--color-bg-overlay-light', value: '#716F6F33', desc: 'Overlay hover/input' },
  { token: '--color-bg-overlay-active', value: '#716F6F55', desc: 'Overlay activo/focus' },
  { token: '--color-bg-sidebar', value: '#0E1323EB', desc: 'Fondo de sidebar/topbar' },
  { token: '--color-bg-input-dark', value: '#3E3A39', desc: 'Fondo input oscuro' },
  { token: '--color-text-primary', value: '#D8D4D2', desc: 'Texto principal' },
  { token: '--color-text-secondary', value: '#D8D4D2E6', desc: 'Texto secundario' },
  { token: '--color-text-tertiary', value: '#D8D4D2B3', desc: 'Texto terciario' },
  { token: '--color-text-muted', value: '#D8D4D280', desc: 'Texto muted' },
  { token: '--color-text-disabled', value: '#D8D4D259', desc: 'Texto deshabilitado' },
  { token: '--body-bg', value: 'var(--color-bg-body)', desc: 'Alias legacy fondo' },
  { token: '--body-bg-gradient', value: 'var(--color-bg-body)', desc: 'Alias legacy fondo' },
  { token: '--body-color', value: 'var(--color-text-primary)', desc: 'Alias legacy texto' },
  { token: '--border-radius', value: '.5rem', desc: 'Radio de bordes' },
  { token: '--sombra', value: '3px 3px 20px #4141412d', desc: 'Sombra general' },
  { token: '--gap', value: '16px', desc: 'Gap de layout principal' },
  { token: '--header-h', value: '64px', desc: 'Altura del header' },
];

const PROD_TOKENS = [
  { token: '--prod-font-xs', value: 'clamp(7px, 0.5vw, 9px)', desc: 'Texto extra small' },
  { token: '--prod-font-sm', value: 'clamp(8px, 0.55vw, 10px)', desc: 'Texto small' },
  { token: '--prod-font-md', value: 'clamp(9px, 0.6vw, 11px)', desc: 'Texto medium' },
  { token: '--prod-font-lg', value: 'clamp(10px, 0.7vw, 14px)', desc: 'Texto large' },
  { token: '--prod-font-xl', value: 'clamp(12px, 0.85vw, 16px)', desc: 'Texto extra large' },
  { token: '--prod-font-xxl', value: 'clamp(14px, 1.1vw, 22px)', desc: 'Titulo grande' },
  { token: '--prod-gap-xs', value: 'clamp(1px, 0.2vw, 3px)', desc: 'Gap minimo' },
  { token: '--prod-gap-sm', value: 'clamp(2px, 0.35vw, 6px)', desc: 'Gap small' },
  { token: '--prod-gap-md', value: 'clamp(4px, 0.5vw, 8px)', desc: 'Gap medium' },
  { token: '--prod-gap-lg', value: 'clamp(6px, 0.6vw, 12px)', desc: 'Gap large' },
  { token: '--prod-padding-sm', value: 'clamp(3px, 0.35vw, 6px)', desc: 'Padding small' },
  { token: '--prod-padding-md', value: 'clamp(4px, 0.5vw, 8px)', desc: 'Padding medium' },
  { token: '--prod-padding-lg', value: 'clamp(6px, 0.6vw, 12px)', desc: 'Padding large' },
  { token: '--prod-radius-sm', value: 'clamp(3px, 0.3vw, 5px)', desc: 'Border radius small' },
  { token: '--prod-radius-md', value: 'clamp(4px, 0.4vw, 8px)', desc: 'Border radius medium' },
  { token: '--prod-radius-lg', value: 'clamp(6px, 0.5vw, 10px)', desc: 'Border radius large' },
];

const GRADIENT_TOKENS = [
  { token: '--azuldeg', bg: 'linear-gradient(to left, #F18F05, transparent)', desc: 'Degradado principal' },
  { token: '--rojodeg', bg: 'linear-gradient(to left, #CC6315, transparent)', desc: 'Degradado acento intenso' },
  { token: '--naranjadeg', bg: 'linear-gradient(to left, #F18F05, transparent)', desc: 'Degradado acento' },
  { token: '--azuloscdeg', bg: 'linear-gradient(to right, #F18F05, #0E1323)', desc: 'Degradado a fondo' },
  { token: '--azulcladeg', bg: 'linear-gradient(to right, #F18F05, #716F6F)', desc: 'Degradado a neutral' },
  { token: '--status-success-deg', bg: 'linear-gradient(to left, #4CAF50, transparent)', desc: 'Degradado estado verde' },
  { token: '--status-warning-deg', bg: 'linear-gradient(to left, #F97316, transparent)', desc: 'Degradado estado naranja' },
  { token: '--status-error-deg', bg: 'linear-gradient(to left, #EF4444, transparent)', desc: 'Degradado estado rojo' },
];

const SPACING_VALUES = [
  { label: '4px', size: 4 },
  { label: '8px', size: 8 },
  { label: '12px', size: 12 },
  { label: '16px (--gap)', size: 16 },
  { label: '24px', size: 24 },
  { label: '32px', size: 32 },
  { label: '48px', size: 48 },
];

const CSS_PREFIXES = [
  { prefix: '.csx-*', desc: 'Componentes comunes reutilizables (cards, filtros, alertas)', example: '.csx-card, .csx-filter-dropdown, .csx-alertas-dot' },
  { prefix: '.prod-*', desc: 'Pantalla Proceso Produccion', example: '.prod-shell, .prod-grid-12x12, .prod-kpi-1' },
  { prefix: '.monitor-*', desc: 'Layout de monitorizacion (shell, main, header, tabs)', example: '.monitor-shell, .monitor-main, .monitor-tab' },
  { prefix: '.tactico-*', desc: 'Pantalla Proceso Plataforma', example: '.tactico-grid-12x12, .tactico-filters-bar' },
  { prefix: '.sorter-*', desc: 'Componentes del Sorter', example: '.sorter-combinado-row, .sorter-curva-content' },
  { prefix: '.indicadores-*', desc: 'KPIs e indicadores', example: '.indicadores-kpis-grid, .indicadores-actividad' },
  { prefix: '.topbar__*', desc: 'Elementos del top bar', example: '.topbar__back, .topbar__back-text' },
  { prefix: '.sidenav-*', desc: 'Elementos del menu lateral', example: '.sidenav-section, .sidenav-link' },
];

const MODIFIER_PATTERNS = [
  { pattern: '--active / --clickable', desc: 'Estado activo o interactivo' },
  { pattern: '--disabled', desc: 'Estado deshabilitado' },
  { pattern: '--rojo / --naranja / --amarillo / --verde', desc: 'Variantes de color por estado' },
  { pattern: '--with-actions', desc: 'Header de card con acciones a la derecha' },
  { pattern: '--menu-collapsed', desc: 'Layout con menu colapsado' },
];

const CHART_STYLE_RULES = [
  { token: 'Libreria', value: 'Highcharts + highcharts-react-official', desc: 'Stack oficial para React' },
  { token: 'Tipos de chart', value: 'column, line, area, spline, stacked, combo', desc: 'El DS define estilo comun, no un tipo unico' },
  { token: 'Contexto', value: 'Widgets de dashboard (cards oscuras)', desc: 'Escalable a multiples modulos/pantallas' },
  { token: 'Fondo', value: "chart.backgroundColor = 'transparent'", desc: 'Sin fondo embebido en el plot' },
  { token: 'Rejilla Y', value: 'yAxis.gridLineWidth = 1', desc: 'Lineas finas blancas' },
  { token: 'Color rejilla', value: 'rgba(255,255,255,0.42)', desc: 'Contraste bajo en tema oscuro' },
  { token: 'Series', value: 'Barras sin borde + linea limpia', desc: 'Estilo comun para charts de produccion' },
  { token: 'Interaccion', value: 'Tooltip compartido + leyenda clicable', desc: 'Hover de puntos y toggle de series' },
];

// Paleta temporal para DS. Sustituir cuando llegue la paleta final de negocio.
const CHART_COLORS = [
  { name: 'Serie 01 - Ano actual', value: '#1A5FB9', token: '--chart-series-01', usage: 'Barras principales' },
  { name: 'Serie 02 - Comparativa', value: '#5AA9E5', token: '--chart-series-02', usage: 'Barras comparativas' },
  { name: 'Serie 01 - Proyeccion', value: '#7CC9D4', token: '--chart-series-03', usage: 'Tramos de proyeccion' },
  { name: 'Serie 02 - Proyeccion', value: '#B6CDD2', token: '--chart-series-04', usage: 'Comparativa proyectada' },
  { name: 'Serie linea - Planificado', value: '#FF8A00', token: '--chart-series-05', usage: 'Linea de referencia/plan' },
];

const CHART_RANGES = [
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mes' },
  { id: 'year', label: 'Ano' },
];

const CHART_FAKE_DATA = {
  week: {
    title: 'Historico - Total',
    categories: ['03/03', '04/03', '05/03', '06/03', '07/03', '09/03'],
    actual: [142000, 175000, 151000, 151000, 102000, 160000],
    compare: [159000, 146000, 183000, 180000, 129000, 177000],
    plan: [152000, 155000, 157000, 162000, 136000, 170000],
    forecastFrom: 5,
  },
  month: {
    title: 'Historico - Total',
    categories: ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6'],
    actual: [690000, 735000, 712000, 765000, 781000, 799000],
    compare: [650000, 705000, 724000, 748000, 759000, 786000],
    plan: [680000, 715000, 730000, 755000, 775000, 795000],
    forecastFrom: 4,
  },
  year: {
    title: 'Historico - Total',
    categories: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep'],
    actual: [2410000, 2490000, 2580000, 2710000, 2790000, 2850000, 2940000, 3020000, 3130000],
    compare: [2330000, 2440000, 2520000, 2620000, 2700000, 2790000, 2860000, 2950000, 3040000],
    plan: [2360000, 2470000, 2550000, 2680000, 2740000, 2820000, 2890000, 2990000, 3090000],
    forecastFrom: 7,
  },
};

function buildChartSeries(values, baseColor, projectedColor, forecastFrom) {
  return values.map((value, idx) => {
    if (idx >= forecastFrom) {
      return { y: value, color: projectedColor };
    }
    return { y: value, color: baseColor };
  });
}

const MAP_STYLE_RULES = [
  { token: 'Libreria', value: 'Mapbox GL JS', desc: 'Motor de mapas interactivos en web' },
  { token: 'Contexto', value: 'Mapas embebidos en widgets de dashboard', desc: 'Mismo lenguaje visual del DS oscuro' },
  { token: 'Tema base', value: 'Dark map + capas de datos', desc: 'Mapa limpio con overlays funcionales' },
  { token: 'Capas', value: 'sources + layers (raster, circle, line, fill)', desc: 'Permite componer mapas por caso de uso' },
  { token: 'Interaccion', value: 'zoom, pan, click, hover, popups', desc: 'Exploracion de puntos, rutas y areas' },
  { token: 'Custom', value: 'Mapas personalizados por estilo y data', desc: 'Se pueden crear mapas tematicos a medida' },
];

const MAP_LAYER_COLORS = [
  { name: 'Puntos operativos', value: '#29A3FF', token: '--map-point-active', usage: 'Ubicaciones activas' },
  { name: 'Puntos alerta', value: '#FF8A00', token: '--map-point-warning', usage: 'Ubicaciones en alerta' },
  { name: 'Halo puntos', value: 'rgba(41,163,255,0.24)', token: '--map-point-halo', usage: 'Glow de foco sobre mapa oscuro' },
  { name: 'Ruta', value: '#7CC9D4', token: '--map-route', usage: 'Lineas de flujo/ruta' },
];

const MAP_DARK_STYLE = {
  version: 8,
  sources: {
    cartoDark: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
    },
  },
  layers: [
    { id: 'carto-dark-base', type: 'raster', source: 'cartoDark' },
  ],
};

const MAP_DEMO_POINTS = [
  { id: 'site-1', name: 'Cad Miralcampo I', status: 'Operativo', color: '#29A3FF', coords: [-3.43, 40.55] },
  { id: 'site-2', name: 'Cad San Fernando', status: 'Alerta', color: '#FF8A00', coords: [-3.53, 40.43] },
  { id: 'site-3', name: 'Cad Getafe', status: 'Operativo', color: '#29A3FF', coords: [-3.74, 40.31] },
  { id: 'site-4', name: 'Cad Vallecas', status: 'Operativo', color: '#29A3FF', coords: [-3.61, 40.39] },
];

const MAP_INITIAL_VIEW = {
  center: [-3.62, 40.43],
  zoom: 9.7,
  pitch: 35,
  bearing: -12,
};

// --------------- SHARED COMPONENTS ---------------

function Swatch({ name, value, token, usage, swatchBackground, swatchText }) {
  const swatchStyle = swatchBackground
    ? { background: swatchBackground, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { background: value };

  return (
    <div className="ds-swatch">
      <div className="ds-swatch__color" style={swatchStyle}>
        {swatchText && <span style={{ color: value, fontSize: 16, fontWeight: 600 }}>{swatchText}</span>}
      </div>
      <div className="ds-swatch__info">
        <span className="ds-swatch__name">{name}</span>
        <span className="ds-swatch__value">Token: {token || 'N/A'}</span>
        <span className="ds-swatch__value">Valor: {value}</span>
        <span className="ds-swatch__value">Uso: {usage || 'General'}</span>
      </div>
    </div>
  );
}

function TokenTable({ tokens }) {
  return (
    <table className="ds-token-table">
      <thead>
        <tr>
          <th>Token</th>
          <th>Valor</th>
          <th>Descripcion</th>
        </tr>
      </thead>
      <tbody>
        {tokens.map((t) => (
          <tr key={t.token}>
            <td>{t.token}</td>
            <td>{t.value}</td>
            <td>{t.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CodeBlock({ children }) {
  return <code className="ds-code">{children}</code>;
}

// --------------- SECTION COMPONENTS ---------------

function ColorsSection() {
  return (
    <>
      <h2 className="ds-section__title">Colores</h2>
      <p className="ds-section__desc">
        Paleta de colores del sistema. CSS custom properties definidas en <code>:root</code> en <code>index.css</code>.
        Tema oscuro con texto claro y colores funcionales por estado, fondo y tipografia.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Paleta principal</h3>
        <div className="ds-swatches">
          {MAIN_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Colores de estado</h3>
        <div className="ds-swatches">
          {STATUS_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Fondos</h3>
        <div className="ds-swatches">
          {BG_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Texto</h3>
        <div className="ds-swatches">
          {TEXT_COLORS.map((c) => <Swatch key={c.name} {...c} swatchBackground="#0E1323" swatchText="Aa" />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Degradados</h3>
        <div className="ds-swatches">
          {GRADIENT_TOKENS.map((g) => (
            <Swatch
              key={g.token}
              name={g.desc}
              value={g.bg}
              token={g.token}
              usage="Degradado utilitario"
            />
          ))}
        </div>
      </div>
    </>
  );
}

function TypographySection() {
  return (
    <>
      <h2 className="ds-section__title">Tipografia</h2>
      <p className="ds-section__desc">
        Fuente principal: <strong>Raleway</strong> (sans-serif). Se usa <code>font-variant-numeric: tabular-nums lining-nums</code> para
        datos numericos alineados. Monospace: <code>source-code-pro, Menlo, Monaco, Consolas</code>.
      </p>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">H1 - Heading 1</div>
        <h1 className="ds-type-specimen__sample" style={{ fontSize: '2rem', fontWeight: 300, margin: 0 }}>Produccion Plataformas</h1>
        <div className="ds-type-specimen__meta">font-size: 2rem | font-weight: 300</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">H2 - Heading 2</div>
        <h2 className="ds-type-specimen__sample" style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>Real vs Planificado</h2>
        <div className="ds-type-specimen__meta">font-size: 1.5rem | font-weight: 600</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">H4 - Card Header</div>
        <h4 className="ds-type-specimen__sample" style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Nivel de Servicio</h4>
        <div className="ds-type-specimen__meta">font-size: 18px | font-weight: 500</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">H5 - Section Label (Uppercase)</div>
        <h5 className="ds-type-specimen__sample" style={{ fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase', margin: 0 }}>Recirculacion Motivos</h5>
        <div className="ds-type-specimen__meta">font-size: 0.75rem | font-weight: 500 | text-transform: uppercase</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">Body</div>
        <p className="ds-type-specimen__sample" style={{ fontSize: 16, fontWeight: 400, margin: 0 }}>
          La produccion acumulada de la jornada es de 12.450 bultos procesados.
        </p>
        <div className="ds-type-specimen__meta">font-size: 16px | font-weight: 400</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">KPI Value (tabular-nums)</div>
        <p className="ds-type-specimen__sample" style={{ fontSize: 28, fontWeight: 700, margin: 0, fontVariantNumeric: 'tabular-nums lining-nums' }}>
          12.450 / 15.200
        </p>
        <div className="ds-type-specimen__meta">font-weight: 700 | font-variant-numeric: tabular-nums lining-nums</div>
      </div>

      <div className="ds-type-specimen">
        <div className="ds-type-specimen__label">Pesos disponibles</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 8 }}>
          {[300, 400, 500, 600, 700, 800].map((w) => (
            <span key={w} style={{ fontWeight: w, fontSize: 16, color: '#fff' }}>{w} - Raleway</span>
          ))}
        </div>
      </div>
    </>
  );
}

function TokensSection() {
  return (
    <>
      <h2 className="ds-section__title">Tokens CSS</h2>
      <p className="ds-section__desc">
        Custom properties definidas en <code>:root</code>. Tokens globales (toda la app) y
        tokens de Produccion (responsive con <code>clamp()</code>).
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Globales</h3>
        <TokenTable tokens={GLOBAL_TOKENS} />
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Produccion (responsive)</h3>
        <TokenTable tokens={PROD_TOKENS} />
      </div>
    </>
  );
}

function SpacingSection() {
  return (
    <>
      <h2 className="ds-section__title">Espaciado</h2>
      <p className="ds-section__desc">
        Gap principal de layout: <code>16px</code> (<code>var(--gap)</code>). Las pantallas de Produccion usan
        tokens responsive con <code>clamp()</code> para adaptar gaps y paddings al viewport.
      </p>

      <div className="ds-spacing-demo">
        {SPACING_VALUES.map((s) => (
          <div className="ds-spacing-block" key={s.label}>
            <div className="ds-spacing-block__bar" style={{ width: s.size, height: s.size }} />
            <span className="ds-spacing-block__label">{s.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function GridSection() {
  return (
    <>
      <h2 className="ds-section__title">Grid 12x12</h2>
      <p className="ds-section__desc">
        Layout principal con CSS Grid de 12 columnas x 12 filas con <code>1fr</code>.
        Clases: <code>.csx-grid-12x12</code>, <code>.prod-grid-12x12</code>, <code>.tactico-grid-12x12</code>.
        Los items se posicionan con <code>grid-column</code> y <code>grid-row</code>.
      </p>

      <div className="ds-preview">
        <div className="ds-preview__label">12 columnas</div>
        <div className="ds-grid-demo">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="ds-grid-demo__cell">{i + 1}</div>
          ))}
        </div>
      </div>

      <CodeBlock>{`/* Definicion del grid */
.csx-grid-12x12 {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: repeat(12, 1fr);
  gap: var(--gap);
}

/* Posicionar un item */
.mi-item {
  grid-column: 1 / 5;   /* columnas 1-4 */
  grid-row: 1 / 7;      /* filas 1-6 */
}`}</CodeBlock>
    </>
  );
}

function CardsSection() {
  const [activeHeaderAction, setActiveHeaderAction] = useState('familia');

  return (
    <>
      <h2 className="ds-section__title">Cards</h2>
      <p className="ds-section__desc">
        Componente base <code>.csx-card</code>. Header con titulo y body con contenido.
        Usado en todas las pantallas de monitorizacion y tactico.
      </p>

      <div className="ds-card-grid">
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, minHeight: 180, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', opacity: 0.9, fontSize: 14, fontWeight: 600 }}>
            Card Basica
          </div>
          <div style={{ padding: '12px 16px', flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            Contenido del body
          </div>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, minHeight: 180, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', opacity: 0.9, fontSize: 14, fontWeight: 600 }}>
            Planificado vs Real
          </div>
          <div style={{ padding: '12px 16px', flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>12.450</span>
              <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>/</span>
              <span style={{ fontSize: 20, fontWeight: 500, color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}>15.200</span>
            </div>
            <span style={{ fontSize: 13, color: '#4caf50', fontWeight: 600, marginTop: 4 }}>(+18%)</span>
          </div>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, minHeight: 180, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', opacity: 0.9, fontSize: 14, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Con Acciones</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={`app-btn app-btn--secondary app-btn--sm ${activeHeaderAction === 'global' ? 'app-btn--active' : ''}`}
                onClick={() => setActiveHeaderAction('global')}
              >
                Global
              </button>
              <button
                type="button"
                className={`app-btn app-btn--secondary app-btn--sm ${activeHeaderAction === 'familia' ? 'app-btn--active' : ''}`}
                onClick={() => setActiveHeaderAction('familia')}
              >
                Familia
              </button>
            </div>
          </div>
          <div style={{ padding: '12px 16px', flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
            Contenido con filtros en header ({activeHeaderAction})
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <CodeBlock>{`<div class="csx-card">
  <div class="csx-card__header">Titulo</div>
  <div class="csx-card__body">
    ...contenido...
  </div>
</div>

/* Variantes de header */
.csx-card__header--clickable
.csx-card__header--with-actions

/* Variantes de body */
.csx-card__body.kpis-body   /* Grid 2 columnas para KPIs */`}</CodeBlock>
      </div>
    </>
  );
}

function ButtonsSection() {
  const arrowIcon = (
    <span className="app-btn__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="13 6 19 12 13 18" />
      </svg>
    </span>
  );

  return (
    <>
      <h2 className="ds-section__title">Botones</h2>
      <p className="ds-section__desc">
        Sistema de botones estandar con variantes primary/secondary/tertiary, versiones con icono,
        icon-only, estados y dos tamaños.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Variantes base</h3>
        <div className="ds-preview ds-preview--dark">
          <div className="ds-preview__row">
            <button className="app-btn app-btn--primary">Primary</button>
            <button className="app-btn app-btn--secondary">Secondary</button>
            <button className="app-btn app-btn--tertiary">Tertiary</button>
            <button className="app-btn app-btn--primary app-btn--with-icon">
              {arrowIcon}
              Primary with icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--with-icon">
              {arrowIcon}
              Secondary with icon
            </button>
            <button className="app-btn app-btn--tertiary app-btn--with-icon">
              {arrowIcon}
              Tertiary with icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--icon-only" aria-label="Icon only">
              {arrowIcon}
            </button>
          </div>
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Estados</h3>
        <div className="ds-preview ds-preview--dark">
          <div className="ds-preview__row">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 84 }}>Primary</span>
            <button className="app-btn app-btn--primary">Default</button>
            <button className="app-btn app-btn--primary app-btn--hover">Hover</button>
            <button className="app-btn app-btn--primary app-btn--active">Active</button>
            <button className="app-btn app-btn--primary app-btn--disabled" disabled>Disabled</button>
          </div>
          <div className="ds-preview__row">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 84 }}>Secondary</span>
            <button className="app-btn app-btn--secondary">Default</button>
            <button className="app-btn app-btn--secondary app-btn--hover">Hover</button>
            <button className="app-btn app-btn--secondary app-btn--active">Active</button>
            <button className="app-btn app-btn--secondary app-btn--disabled" disabled>Disabled</button>
          </div>
          <div className="ds-preview__row">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 84 }}>Tertiary</span>
            <button className="app-btn app-btn--tertiary">Default</button>
            <button className="app-btn app-btn--tertiary app-btn--hover">Hover</button>
            <button className="app-btn app-btn--tertiary app-btn--active">Active</button>
            <button className="app-btn app-btn--tertiary app-btn--disabled" disabled>Disabled</button>
          </div>
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Tamaños</h3>
        <div className="ds-preview ds-preview--dark">
          <div className="ds-preview__row">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 64 }}>SM</span>
            <button className="app-btn app-btn--primary app-btn--sm">Primary sm</button>
            <button className="app-btn app-btn--secondary app-btn--sm">Secondary sm</button>
            <button className="app-btn app-btn--tertiary app-btn--sm">Tertiary sm</button>
            <button className="app-btn app-btn--primary app-btn--with-icon app-btn--sm">
              {arrowIcon}
              Primary icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--with-icon app-btn--sm">
              {arrowIcon}
              Secondary icon
            </button>
            <button className="app-btn app-btn--tertiary app-btn--with-icon app-btn--sm">
              {arrowIcon}
              Tertiary icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--icon-only app-btn--sm" aria-label="Icon sm">{arrowIcon}</button>
          </div>
          <div className="ds-preview__row">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 64 }}>LG</span>
            <button className="app-btn app-btn--primary app-btn--lg">Primary lg</button>
            <button className="app-btn app-btn--secondary app-btn--lg">Secondary lg</button>
            <button className="app-btn app-btn--tertiary app-btn--lg">Tertiary lg</button>
            <button className="app-btn app-btn--primary app-btn--with-icon app-btn--lg">
              {arrowIcon}
              Primary icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--with-icon app-btn--lg">
              {arrowIcon}
              Secondary icon
            </button>
            <button className="app-btn app-btn--tertiary app-btn--with-icon app-btn--lg">
              {arrowIcon}
              Tertiary icon
            </button>
            <button className="app-btn app-btn--secondary app-btn--icon-only app-btn--lg" aria-label="Icon lg">{arrowIcon}</button>
          </div>
        </div>
        <CodeBlock>{`.app-btn { height: 34px; padding: 0 14px; border-radius: 6px; }

/* Variantes */
.app-btn--primary   { background: var(--btn-primary-bg); border-color: var(--btn-primary-border); color: var(--btn-primary-text); }
.app-btn--secondary { background: var(--btn-secondary-bg); border-color: var(--btn-secondary-border); color: var(--btn-secondary-text); }
.app-btn--tertiary  { background: var(--btn-tertiary-bg); border-color: var(--btn-tertiary-border); color: var(--btn-tertiary-text); text-decoration: underline; }

/* Estados */
.app-btn--hover,
.app-btn:hover { /* estilo hover de cada variante */ }
.app-btn--active { /* estilo activo de cada variante */ }
.app-btn--disabled,
.app-btn:disabled { pointer-events: none; cursor: not-allowed; }

/* Iconos y tamaños */
.app-btn--with-icon { gap: 8px; }
.app-btn--icon-only { width: 34px; min-width: 34px; padding: 0; }
.app-btn--sm { height: 28px; padding: 0 10px; }
.app-btn--lg { height: 42px; padding: 0 18px; }`}</CodeBlock>
      </div>
    </>
  );
}
function DropdownsSection() {
  return (
    <>
      <h2 className="ds-section__title">Dropdowns</h2>
      <p className="ds-section__desc">
        Selectores de filtro con clase <code>.csx-filter-dropdown</code> o <code>.tactico-filter-dropdown</code>.
        Son <code>&lt;select&gt;</code> nativos estilizados con fondo semi-transparente y flecha SVG custom.
      </p>

      <div className="ds-preview ds-preview--dark">
        <div className="ds-preview__label">Variantes de dropdown</div>
        <div className="ds-preview__row" style={{ flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Normal</div>
            <select style={{
              padding: '6px 40px 6px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 6, color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: "'Raleway', sans-serif", cursor: 'pointer',
              minWidth: 220, outline: 'none', appearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: 12,
            }}>
              <option>Miralcampo I</option>
              <option>San Fernando</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Hover</div>
            <select style={{
              padding: '6px 40px 6px 16px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 6, color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: "'Raleway', sans-serif", cursor: 'pointer',
              minWidth: 220, outline: 'none', appearance: 'none',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: 12,
            }}>
              <option>Miralcampo I</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Focus</div>
            <select style={{
              padding: '6px 40px 6px 16px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(14,19,35,0.8)',
              borderRadius: 6, color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: "'Raleway', sans-serif", cursor: 'pointer',
              minWidth: 220, outline: 'none', appearance: 'none', boxShadow: '0 0 0 2px rgba(14,19,35,0.3)',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: 12,
            }}>
              <option>Miralcampo I</option>
            </select>
          </div>
        </div>
      </div>

      <CodeBlock>{`.csx-filter-dropdown {
  padding: 6px 16px;
  padding-right: 40px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  appearance: none;
  /* Flecha SVG como background-image */
}
.csx-filter-dropdown:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.3);
}
.csx-filter-dropdown:focus {
  border-color: rgba(14, 19, 35, 0.8);
  box-shadow: 0 0 0 2px rgba(14, 19, 35, 0.3);
}`}</CodeBlock>
    </>
  );
}

function AlertsSection() {
  return (
    <>
      <h2 className="ds-section__title">Alertas & Status</h2>
      <p className="ds-section__desc">
        Indicadores de estado con dots de color y valores con formato positivo/negativo.
        Usados en KPIs, alertas de plataforma y comparativas.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Alert dots</h3>
        <div className="ds-preview ds-preview--dark">
          <div className="ds-dots-row">
            <div className="ds-dot-demo">
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', flexShrink: 0 }} />
              <span>Amarillo - Warning bajo</span>
            </div>
            <div className="ds-dot-demo">
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#f97316', display: 'inline-block', flexShrink: 0 }} />
              <span>Naranja - Warning alto</span>
            </div>
            <div className="ds-dot-demo">
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }} />
              <span>Rojo - Critico</span>
            </div>
          </div>
        </div>
        <CodeBlock>{`.csx-alertas-dot { width: 12px; height: 12px; border-radius: 50%; }
.csx-alertas-dot--amarillo { background: #f59e0b; }
.csx-alertas-dot--naranja  { background: #f97316; }
.csx-alertas-dot--rojo     { background: #ef4444; }`}</CodeBlock>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">KPI con diferencia positiva/negativa</h3>
        <div className="ds-preview ds-preview--dark">
          <div className="ds-preview__row">
            <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Diferencia</div>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#4caf50', fontVariantNumeric: 'tabular-nums' }}>+18%</span>
            </div>
            <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Diferencia</div>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>-7%</span>
            </div>
            <div style={{ padding: '12px 20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Diferencia</div>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>0%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Utilidades de estado</h3>
        <CodeBlock>{`.csx-alertas-dot--amarillo { background: #F59E0B; }
.csx-alertas-dot--naranja  { background: #F97316; }
.csx-alertas-dot--rojo     { background: #EF4444; }`}</CodeBlock>
      </div>
    </>
  );
}

function ChartsSection() {
  const [activeRange, setActiveRange] = useState('week');
  const chartData = CHART_FAKE_DATA[activeRange];

  const chartOptions = React.useMemo(() => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      style: { fontFamily: 'Raleway, sans-serif' },
      spacingTop: 8,
      spacingRight: 8,
      spacingLeft: 0,
      spacingBottom: 8,
      height: 430,
    },
    accessibility: { enabled: false },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories: chartData.categories,
      lineColor: 'rgba(255,255,255,0.55)',
      tickColor: 'transparent',
      labels: {
        style: {
          color: 'rgba(255,255,255,0.95)',
          fontSize: '12px',
          fontWeight: '700',
        },
      },
      crosshair: {
        width: 1,
        color: 'rgba(255,255,255,0.28)',
      },
    },
    yAxis: {
      title: { text: null },
      min: 0,
      tickWidth: 0,
      lineWidth: 0,
      gridLineWidth: 1,
      gridLineColor: 'rgba(255,255,255,0.42)',
      labels: {
        style: {
          color: 'rgba(255,255,255,0.95)',
          fontSize: '12px',
          fontWeight: '700',
        },
        formatter() {
          if (this.value === 0) return '0';
          if (this.value >= 1000000) return `${(this.value / 1000000).toFixed(1)}M`;
          return `${Math.round(this.value / 1000)}k`;
        },
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: 'rgba(14,19,35,0.96)',
      borderColor: 'rgba(255,255,255,0.22)',
      borderRadius: 8,
      style: { color: '#fff', fontSize: '12px' },
      useHTML: true,
      headerFormat: '<span style="font-size:11px">{point.key}</span><br/>',
      pointFormatter() {
        const formatted = Highcharts.numberFormat(this.y, 0, ',', '.');
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formatted}</b><br/>`;
      },
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        color: 'rgba(255,255,255,0.95)',
        fontSize: '18px',
        fontWeight: '700',
      },
      itemHoverStyle: {
        color: '#ffffff',
      },
      symbolWidth: 12,
      symbolHeight: 12,
      symbolRadius: 6,
      y: 8,
    },
    plotOptions: {
      series: {
        borderWidth: 0,
        groupPadding: 0.12,
        pointPadding: 0.08,
        animation: { duration: 320 },
        states: { hover: { brightness: 0.06 } },
        cursor: 'pointer',
      },
      column: {
        borderRadius: 0,
      },
      line: {
        lineWidth: 3,
        marker: {
          enabled: false,
          states: {
            hover: { enabled: true, radius: 4 },
          },
        },
        states: {
          hover: { lineWidthPlus: 0 },
        },
      },
    },
    series: [
      {
        type: 'line',
        name: 'Planificado',
        data: chartData.plan,
        color: CHART_COLORS[4].value,
        zIndex: 3,
      },
      {
        type: 'column',
        name: 'Ano Actual',
        data: buildChartSeries(
          chartData.actual,
          CHART_COLORS[0].value,
          CHART_COLORS[2].value,
          chartData.forecastFrom
        ),
      },
      {
        type: 'column',
        name: 'AA',
        data: buildChartSeries(
          chartData.compare,
          CHART_COLORS[1].value,
          CHART_COLORS[3].value,
          chartData.forecastFrom
        ),
      },
    ],
  }), [chartData]);

  return (
    <>
      <h2 className="ds-section__title">Charts</h2>
      <p className="ds-section__desc">
        Estandar de graficos para dashboard: usamos <code>Highcharts</code> y mantenemos un estilo coherente para multiples
        tipos de chart dentro de cards oscuras. Referencia de ejemplos oficiales:{' '}
        <a className="ds-inline-link" href="https://www.highcharts.com/demo" target="_blank" rel="noopener noreferrer">
          highcharts.com/demo
        </a>.
        La base visual es <strong>sin fondo en la grafica</strong>, con <strong>lineas de rejilla finas en blanco</strong>.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Reglas base</h3>
        <TokenTable tokens={CHART_STYLE_RULES} />
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Paleta provisional para charts</h3>
        <p className="ds-section__desc" style={{ marginBottom: 16, maxWidth: 800 }}>
          Esta paleta es transversal para charts de dashboard (line, column, combo, stacked). Cuando compartas la paleta final, solo hay que actualizar
          <code> CHART_COLORS </code> y <code> colors </code> en la configuracion de Highcharts.
        </p>
        <div className="ds-swatches">
          {CHART_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Preview real con Highcharts (interactivo, combo)</h3>
        <div className="ds-preview ds-preview--dark ds-chart-preview">
          <div className="ds-chart-preview__topbar">
            <h4 className="ds-chart-preview__title">{chartData.title}</h4>
            <div className="ds-chart-preview__ranges">
              {CHART_RANGES.map((range) => (
                <button
                  key={range.id}
                  type="button"
                  className={`ds-chart-preview__range-btn ${activeRange === range.id ? 'is-active' : ''}`}
                  onClick={() => setActiveRange(range.id)}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ds-chart-preview__chart-shell">
            <AnimatedHighcharts
              options={chartOptions}
              containerProps={{ className: 'ds-chart-preview__chart' }}
            />
          </div>

          <p className="ds-chart-preview__hint">
            Interaccion activa: hover para tooltip compartido, click en leyenda para ocultar/mostrar series, y cambios de rango con los botones.
            Este preview es orientativo del estilo dashboard, no una plantilla cerrada de un unico grafico.
          </p>
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Plantilla base de Highcharts</h3>
        <CodeBlock>{`import Highcharts from 'highcharts';
import { AnimatedHighcharts } from './AnimatedHighcharts';
import { useState, useMemo } from 'react';

// Demos oficiales: https://www.highcharts.com/demo

const RANGES = {
  week: {
    categories: ['03/03', '04/03', '05/03', '06/03', '07/03', '09/03'],
    plan: [152000, 155000, 157000, 162000, 136000, 170000],
    actual: [142000, 175000, 151000, 151000, 102000, 160000],
    compare: [159000, 146000, 183000, 180000, 129000, 177000],
  },
  month: { ... },
  year: { ... },
};

function ChartDemo() {
  const [range, setRange] = useState('week');

  const options = useMemo(() => ({
    chart: { type: 'column', backgroundColor: 'transparent' },
    credits: { enabled: false },
    xAxis: { categories: RANGES[range].categories },
    yAxis: {
      title: { text: null },
      gridLineWidth: 1,
      gridLineColor: 'rgba(255,255,255,0.42)',
    },
    tooltip: { shared: true },
    plotOptions: { series: { borderWidth: 0 }, line: { marker: { enabled: false } } },
    series: [
      { type: 'line', name: 'Planificado', data: RANGES[range].plan },
      { name: 'Ano Actual', data: RANGES[range].actual },
      { name: 'AA', data: RANGES[range].compare },
    ],
  }), [range]);

  return <AnimatedHighcharts options={options} />;
}`}</CodeBlock>
      </div>
    </>
  );
}

function MapsSection() {
  const mapContainerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const [mapStatus, setMapStatus] = useState('loading');

  React.useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;

    let hasLoaded = false;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_DARK_STYLE,
      center: MAP_INITIAL_VIEW.center,
      zoom: MAP_INITIAL_VIEW.zoom,
      pitch: MAP_INITIAL_VIEW.pitch,
      bearing: MAP_INITIAL_VIEW.bearing,
      antialias: true,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');

    map.on('load', () => {
      hasLoaded = true;
      setMapStatus('ready');

      const pointsData = {
        type: 'FeatureCollection',
        features: MAP_DEMO_POINTS.map((point) => ({
          type: 'Feature',
          properties: {
            id: point.id,
            name: point.name,
            status: point.status,
            color: point.color,
          },
          geometry: {
            type: 'Point',
            coordinates: point.coords,
          },
        })),
      };

      map.addSource('ds-map-sites', {
        type: 'geojson',
        data: pointsData,
      });

      map.addLayer({
        id: 'ds-map-points-halo',
        type: 'circle',
        source: 'ds-map-sites',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 8,
            11, 16,
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.22,
          'circle-blur': 0.8,
        },
      });

      map.addLayer({
        id: 'ds-map-points',
        type: 'circle',
        source: 'ds-map-sites',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 4,
            11, 8,
          ],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 1,
          'circle-stroke-color': 'rgba(216,212,210,0.88)',
        },
      });

      map.addSource('ds-map-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              MAP_DEMO_POINTS[0].coords,
              MAP_DEMO_POINTS[1].coords,
              MAP_DEMO_POINTS[3].coords,
              MAP_DEMO_POINTS[2].coords,
            ],
          },
        },
      });

      map.addLayer({
        id: 'ds-map-route-line',
        type: 'line',
        source: 'ds-map-route',
        paint: {
          'line-color': '#7CC9D4',
          'line-width': 2.5,
          'line-opacity': 0.85,
        },
      });

      map.on('mouseenter', 'ds-map-points', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'ds-map-points', () => {
        map.getCanvas().style.cursor = '';
      });

      map.on('click', 'ds-map-points', (event) => {
        const feature = event.features && event.features[0];
        if (!feature) return;

        if (popupRef.current) {
          popupRef.current.remove();
        }

        popupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
          offset: [0, -10],
          className: 'ds-map-popup',
        })
          .setLngLat(feature.geometry.coordinates)
          .setHTML(`
            <div class="ds-map-popup__title">${feature.properties.name}</div>
            <div class="ds-map-popup__meta">${feature.properties.status}</div>
          `)
          .addTo(map);
      });
    });

    map.on('error', () => {
      if (!hasLoaded) {
        setMapStatus('error');
      }
    });

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <h2 className="ds-section__title">Maps</h2>
      <p className="ds-section__desc">
        Estandar de mapas para dashboard: usamos <code>Mapbox</code> como motor para visualizaciones geograficas interactivas.
        Referencias oficiales:{' '}
        <a className="ds-inline-link" href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer">
          mapbox.com
        </a>{' '}
        y{' '}
        <a className="ds-inline-link" href="https://docs.mapbox.com/mapbox-gl-js/example/" target="_blank" rel="noopener noreferrer">
          ejemplos Mapbox GL
        </a>.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Reglas base</h3>
        <TokenTable tokens={MAP_STYLE_RULES} />
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Paleta provisional de capas</h3>
        <p className="ds-section__desc" style={{ marginBottom: 16, maxWidth: 820 }}>
          Esta paleta esta pensada para mapas operativos de dashboard. A partir de esta base se pueden crear mapas
          personalizados por caso de uso: rutas, cobertura, heatmaps, clusters y capas por estado.
        </p>
        <div className="ds-swatches">
          {MAP_LAYER_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Preview real con Mapbox (modo oscuro)</h3>
        <div className="ds-preview ds-preview--dark ds-map-preview">
          <div className="ds-map-preview__topbar">
            <h4 className="ds-map-preview__title">Mapa operativo - Zona centro</h4>
            <span className="ds-map-preview__badge">Dark mode</span>
          </div>

          <div className="ds-map-preview__map-shell">
            <div ref={mapContainerRef} className="ds-map-preview__map" />
            {mapStatus === 'loading' && (
              <div className="ds-map-preview__overlay">Cargando mapa...</div>
            )}
            {mapStatus === 'error' && (
              <div className="ds-map-preview__overlay ds-map-preview__overlay--error">
                No se pudo cargar el mapa
              </div>
            )}
          </div>

          <p className="ds-map-preview__hint">
            Interaccion activa: zoom, pan, inclinacion, click en puntos para popup y controles de navegacion.
          </p>
        </div>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Plantilla base de Mapbox</h3>
        <CodeBlock>{`import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef } from 'react';

const DARK_STYLE = {
  version: 8,
  sources: {
    darkBase: {
      type: 'raster',
      tiles: ['https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'],
      tileSize: 256,
    },
  },
  layers: [{ id: 'base', type: 'raster', source: 'darkBase' }],
};

function MapDemo() {
  const containerRef = useRef(null);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: DARK_STYLE,
      center: [-3.62, 40.43],
      zoom: 9.7,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    return () => map.remove();
  }, []);

  return <div ref={containerRef} style={{ height: 420 }} />;
}`}</CodeBlock>
      </div>
    </>
  );
}

function NavigationSection() {
  return (
    <>
      <h2 className="ds-section__title">Navegacion</h2>
      <p className="ds-section__desc">
        El layout de monitorizacion usa <code>MonitorLayout</code> que envuelve <code>TopBar</code>, <code>SideNav</code> y el contenido principal.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Estructura de layout</h3>
        <CodeBlock>{`<MonitorLayout>
  <SideNav isCollapsed={...} onToggle={...} />
  <TopBar isMenuCollapsed={...} onMenuToggle={...} />
  <main class="monitor-main">
    {children}
  </main>
</MonitorLayout>

/* Clases */
.monitor-shell  /* contenedor raiz */
.monitor-main   /* area principal */
.monitor-main--menu-collapsed  /* menu colapsado */`}</CodeBlock>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">TopBar (preview)</h3>
        <div className="ds-preview ds-preview--dark">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'rgba(14,19,35,0.92)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Proceso Plataforma</span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{'Atr\u00E1s'}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ScrollbarSection() {
  return (
    <>
      <h2 className="ds-section__title">Scrollbar</h2>
      <p className="ds-section__desc">
        La barra de scroll custom se aplica tanto al <code>SideNav</code> como al contenido principal.
        Mantiene el mismo grosor, thumb y hover para coherencia visual.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Sidebar + Content</h3>
        <CodeBlock>{`.ds-sidebar__nav,
.ds-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.25) transparent;
}

.ds-sidebar__nav::-webkit-scrollbar,
.ds-content::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.ds-sidebar__nav::-webkit-scrollbar-thumb,
.ds-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.ds-sidebar__nav::-webkit-scrollbar-thumb:hover,
.ds-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}`}</CodeBlock>
      </div>
    </>
  );
}

function LoadingSection() {
  return (
    <>
      <h2 className="ds-section__title">Estados de carga</h2>
      <p className="ds-section__desc">
        Mensajes que se muestran mientras los datos se obtienen de la API. Centrados, con opacidad reducida.
      </p>

      <div className="ds-preview ds-preview--dark">
        <div className="ds-preview__label">Cargando</div>
        <div style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
          Cargando datos...
        </div>
      </div>

      <div className="ds-preview ds-preview--dark" style={{ marginTop: 16 }}>
        <div className="ds-preview__label">Sin datos</div>
        <div style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
          No hay datos disponibles
        </div>
      </div>

      <div className="ds-preview ds-preview--dark" style={{ marginTop: 16 }}>
        <div className="ds-preview__label">Error</div>
        <div style={{ padding: 20, textAlign: 'center', color: '#ff6b6b' }}>
          No se pudo cargar la curva del sorter
        </div>
      </div>

      <CodeBlock>{`/* Clases de estado */
.curva-loading    { color: rgba(255,255,255,0.7); text-align: center; }
.curva-error      { color: #ff6b6b; text-align: center; }
.barras-loading   { /* idem */ }
.actividad-loading { /* idem */ }`}</CodeBlock>
    </>
  );
}

function IconsSection() {
  return (
    <>
      <h2 className="ds-section__title">Iconos</h2>
      <p className="ds-section__desc">
        SVG inline en los componentes. No hay libreria externa.
        Patron comun: <code>viewBox="0 0 24 24"</code> con <code>stroke="currentColor"</code>.
      </p>

      <div className="ds-preview ds-preview--dark">
        <div className="ds-preview__label">Iconos de navegacion (SVG inline)</div>
        <div className="ds-preview__row" style={{ gap: 32 }}>
          {[
            { name: 'Menu', paths: <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></> },
            { name: 'Back', paths: <polyline points="15 18 9 12 15 6" /> },
            { name: 'Forward', paths: <polyline points="9 18 15 12 9 6" /> },
            { name: 'Close', paths: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> },
            { name: 'Chevron', paths: <polyline points="6 9 12 15 18 9" /> },
          ].map((icon) => (
            <div key={icon.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                {icon.paths}
              </svg>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{icon.name}</span>
            </div>
          ))}
        </div>
      </div>

      <CodeBlock>{`<svg width="18" height="18" viewBox="0 0 24 24"
     fill="none" stroke="currentColor" strokeWidth="2">
  <polyline points="15 18 9 12 15 6" />
</svg>
/* Hereda color del padre via currentColor */`}</CodeBlock>
    </>
  );
}

function NamingSection() {
  return (
    <>
      <h2 className="ds-section__title">Convenciones CSS</h2>
      <p className="ds-section__desc">
        Naming BEM-like con prefijos por dominio. Componentes comunes usan <code>.csx-*</code>,
        cada pantalla tiene su propio prefijo.
      </p>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Prefijos por dominio</h3>
        <table className="ds-token-table">
          <thead><tr><th>Prefijo</th><th>Dominio</th><th>Ejemplos</th></tr></thead>
          <tbody>
            {CSS_PREFIXES.map((p) => (
              <tr key={p.prefix}>
                <td>{p.prefix}</td>
                <td style={{ color: 'rgba(255,255,255,0.8)' }}>{p.desc}</td>
                <td style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{p.example}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Modificadores comunes</h3>
        <table className="ds-token-table">
          <thead><tr><th>Modificador</th><th>Uso</th></tr></thead>
          <tbody>
            {MODIFIER_PATTERNS.map((m) => (
              <tr key={m.pattern}>
                <td>{m.pattern}</td>
                <td style={{ color: 'rgba(255,255,255,0.8)' }}>{m.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ds-subsection">
        <h3 className="ds-subsection__title">Estructura de archivos CSS</h3>
        <CodeBlock>{`src/
  index.css                          /* Variables globales, reset */
  styles/
    monitor-layout.css               /* Layout de MonitorLayout */
    monitor.css                      /* Estilos compartidos */
  pantallas/
    monitorizacion/
      Produccion.layout.css          /* Grid + tokens Produccion */
      Recepcion.layout.css           /* Grid Recepcion */
      Expedicion.layout.css          /* Grid Expedicion */
    cadena-suministro/
      CadenaSuministro.layout.css    /* Layout + .csx-* */
    tactico/
      TacticoOrquestador.layout.css  /* Grid + .tactico-* */`}</CodeBlock>
      </div>
    </>
  );
}

// --------------- SECTION MAP ---------------

const SECTION_MAP = {
  colors: ColorsSection,
  typography: TypographySection,
  tokens: TokensSection,
  spacing: SpacingSection,
  grid: GridSection,
  cards: CardsSection,
  buttons: ButtonsSection,
  dropdowns: DropdownsSection,
  alerts: AlertsSection,
  charts: ChartsSection,
  maps: MapsSection,
  navigation: NavigationSection,
  scrollbar: ScrollbarSection,
  loading: LoadingSection,
  icons: IconsSection,
  naming: NamingSection,
};

// --------------- MAIN ---------------

export default function DesignSystem() {
  const [activeSection, setActiveSection] = useState('colors');

  const ActiveComponent = SECTION_MAP[activeSection];

  return (
    <div className="ds-page">
      <div className="ds-layout">
        <aside className="ds-sidebar">
          {/* Header con logo - igual que sidenav__header */}
          <div className="ds-sidebar__header">
            <div>
              <div className="ds-sidebar__subtitle">VPO Design System</div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="ds-sidebar__nav">
            <div className="ds-sidebar__group-title">Foundations</div>
            {SECTIONS.foundations.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`ds-sidebar__link ${activeSection === s.id ? 'ds-sidebar__link--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </button>
            ))}
            <div className="ds-sidebar__group-title">Components</div>
            {SECTIONS.components.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`ds-sidebar__link ${activeSection === s.id ? 'ds-sidebar__link--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </button>
            ))}
            <div className="ds-sidebar__group-title">Guidelines</div>
            {SECTIONS.guidelines.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`ds-sidebar__link ${activeSection === s.id ? 'ds-sidebar__link--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="ds-sidebar__footer">
            <Link to="/" className="app-btn app-btn--tertiary app-btn--sm app-btn--arrow-left ds-sidebar__back">
              {'Atr\u00E1s'}
            </Link>
          </div>
        </aside>

        <div className="ds-content">
          <div className="ds-section" key={activeSection}>
            {ActiveComponent && <ActiveComponent />}
          </div>
        </div>
      </div>
    </div>
  );
}


